import React, { useEffect, useState, useTransition } from "react";
import {
  Bot,
  Filter,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { fetchConversationBots, fetchConversationOverview } from "../../api";
import type { BotMetadata, ConversationOverviewPayload } from "../../types";
import { ConversationKpiGrid } from "./ConversationKpiGrid";
import { ConversationDistributionChart } from "./ConversationDistributionChart";
import { ConversationDailyTrendChart } from "./ConversationDailyTrendChart";
import { ConversationAgentTable } from "./ConversationAgentTable";
import { ConversationLostReasonsTable } from "./ConversationLostReasonsTable";
import { ConversationReviewPanel } from "./ConversationReviewPanel";

interface ClientConversationsTabProps {
  clientSlug: string;
  initialBots?: BotMetadata[];
}

export const ClientConversationsTab: React.FC<ClientConversationsTabProps> = ({
  clientSlug,
  initialBots,
}) => {
  const [bots, setBots] = useState<BotMetadata[]>(initialBots ?? []);
  const [selectedBotId, setSelectedBotId] = useState<string>("ALL");
  const [selectedChannel, setSelectedChannel] = useState<string>("ALL");
  const [includeTest, setIncludeTest] = useState<boolean>(false);

  // Rango de fechas por defecto: últimos 30 días
  const todayStr = new Date().toISOString().split("T")[0];
  const d30AgoStr = new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0];

  const [startDate, setStartDate] = useState<string>(d30AgoStr);
  const [endDate, setEndDate] = useState<string>(todayStr);

  const [overview, setOverview] = useState<ConversationOverviewPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  const setQuickRange = (days: number) => {
    const end = todayStr;
    const start = new Date(Date.now() - days * 86400000).toISOString().split("T")[0];
    setStartDate(start);
    setEndDate(end);
  };

  // Cargar bots si no vienen o al cambiar clientSlug
  useEffect(() => {
    let active = true;
    if (initialBots && initialBots.length > 0) {
      setBots(initialBots);
      return;
    }
    fetchConversationBots(clientSlug)
      .then((data) => {
        if (!active) return;
        setBots(data);
        if (data.length > 0 && selectedBotId !== "ALL" && !data.some((b) => b.bot_id === selectedBotId)) {
          setSelectedBotId("ALL");
        }
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error al cargar bots del cliente:", err);
      });

    return () => {
      active = false;
    };
  }, [clientSlug, initialBots]);

  // Cargar overview
  const loadData = () => {
    if (bots.length === 0 && initialBots !== undefined && initialBots.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetchConversationOverview(clientSlug, {
      botId: selectedBotId,
      channel: selectedChannel,
      startDate: startDate,
      endDate: endDate,
      includeTest: includeTest,
    })
      .then((payload) => {
        startTransition(() => {
          setOverview(payload);
          setLoading(false);
        });
      })
      .catch((err) => {
        console.error("Error al cargar métricas de conversaciones:", err);
        setError(err.message || "Error consultando BigQuery");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
  }, [clientSlug, selectedBotId, selectedChannel, startDate, endDate, includeTest, bots.length]);

  // Si no hay bots configurados en dim_bot para este cliente
  if (!loading && bots.length === 0) {
    return (
      <div
        className="conversations-empty-container"
        style={{
          padding: "56px 24px",
          textAlign: "center",
          background: "var(--bg-surface, #111827)",
          borderRadius: "8px",
          border: "1px solid var(--border-subtle, rgba(255, 255, 255, 0.08))",
          margin: "16px 0",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            padding: "16px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.05)",
            marginBottom: "16px",
            color: "var(--text-muted, #9ca3af)",
          }}
        >
          <Bot size={44} />
        </div>
        <h3 style={{ fontSize: "18px", fontWeight: 600, margin: "0 0 8px", color: "var(--text-primary, #f9fafb)" }}>
          Sin bots conectados
        </h3>
        <p style={{ color: "var(--text-muted, #9ca3af)", margin: 0, fontSize: "14px", maxWidth: "480px", marginInline: "auto" }}>
          Este cliente no posee bots configurados en <code style={{ color: "var(--text-primary)" }}>dim_bot</code>.
        </p>
      </div>
    );
  }

  const selectedBotMeta = bots.find((b) => b.bot_id === selectedBotId);
  const activeTimezone = selectedBotMeta?.timezone || "America/Santiago";

  return (
    <div className="client-conversations-tab" style={{ marginTop: "12px" }}>
      {/* Barra de Filtros */}
      <div
        style={{
          background: "var(--bg-surface, #111827)",
          border: "1px solid var(--border-subtle, rgba(255, 255, 255, 0.08))",
          borderRadius: "8px",
          padding: "14px 18px",
          marginBottom: "20px",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "14px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--text-muted)" }}>
          <Filter size={16} />
          <span style={{ fontSize: "13px", fontWeight: 600 }}>Filtros:</span>
        </div>

        {/* Selector de Bot (solo si hay más de 1 bot) */}
        {bots.length > 1 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label style={{ fontSize: "11px", textTransform: "uppercase", color: "var(--text-muted)" }}>Bot</label>
            <select
              value={selectedBotId}
              onChange={(e) => setSelectedBotId(e.target.value)}
              style={{
                padding: "6px 10px",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                borderRadius: "6px",
                color: "var(--text-primary)",
                fontSize: "13px",
              }}
            >
              <option value="ALL">Todos los bots ({bots.length})</option>
              {bots.map((b) => (
                <option key={b.bot_id} value={b.bot_id}>
                  {b.bot_name || b.bot_id}
                </option>
              ))}
            </select>
          </div>
        ) : bots.length === 1 ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px", background: "rgba(59, 130, 246, 0.1)", borderRadius: "6px", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
            <Bot size={15} style={{ color: "#3b82f6" }} />
            <span style={{ fontSize: "13px", fontWeight: 500, color: "var(--text-primary)" }}>
              {bots[0].bot_name || bots[0].bot_id}
            </span>
          </div>
        ) : null}

        {/* Selector de Canal */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontSize: "11px", textTransform: "uppercase", color: "var(--text-muted)" }}>Canal</label>
          <select
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            style={{
              padding: "6px 10px",
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              borderRadius: "6px",
              color: "var(--text-primary)",
              fontSize: "13px",
            }}
          >
            <option value="ALL">Todos los canales</option>
            <option value="wa">WhatsApp (wa)</option>
            <option value="tg">Telegram (tg)</option>
            <option value="web">Web Simulador (web)</option>
          </select>
        </div>

        {/* Rango de Fechas */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontSize: "11px", textTransform: "uppercase", color: "var(--text-muted)" }}>Desde</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{
              padding: "5px 8px",
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              borderRadius: "6px",
              color: "var(--text-primary)",
              fontSize: "13px",
            }}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontSize: "11px", textTransform: "uppercase", color: "var(--text-muted)" }}>Hasta</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{
              padding: "5px 8px",
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              borderRadius: "6px",
              color: "var(--text-primary)",
              fontSize: "13px",
            }}
          />
        </div>

        {/* Atajos Rápidos */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: "6px", paddingTop: "18px" }}>
          <button
            type="button"
            onClick={() => setQuickRange(7)}
            style={{
              padding: "5px 10px",
              borderRadius: "4px",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "var(--text-primary)",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            7d
          </button>
          <button
            type="button"
            onClick={() => setQuickRange(30)}
            style={{
              padding: "5px 10px",
              borderRadius: "4px",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "var(--text-primary)",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            30d
          </button>
          <button
            type="button"
            onClick={() => setQuickRange(90)}
            style={{
              padding: "5px 10px",
              borderRadius: "4px",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "var(--text-primary)",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            90d
          </button>
        </div>

        {/* Checkbox Incluir Pruebas */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px", paddingTop: "18px", marginLeft: "auto" }}>
          <input
            type="checkbox"
            id="client-include-test-check"
            checked={includeTest}
            onChange={(e) => setIncludeTest(e.target.checked)}
            style={{ cursor: "pointer" }}
          />
          <label htmlFor="client-include-test-check" style={{ fontSize: "12px", color: "var(--text-muted)", cursor: "pointer" }}>
            Incluir pruebas (web)
          </label>
        </div>

        {/* Botón de refrescar */}
        <button
          onClick={loadData}
          disabled={loading}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 12px",
            borderRadius: "6px",
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid var(--border-subtle, rgba(255,255,255,0.1))",
            color: "var(--text-primary, #f9fafb)",
            fontSize: "13px",
            cursor: loading ? "not-allowed" : "pointer",
            fontWeight: 500,
            marginTop: "18px",
          }}
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          <span>{loading ? "Cargando..." : "Actualizar"}</span>
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: "14px 16px",
            borderRadius: "8px",
            background: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            color: "#f87171",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {loading && !overview && (
        <div style={{ padding: "48px 16px", textAlign: "center", color: "var(--text-muted, #9ca3af)" }}>
          <RefreshCw size={22} className="spin" style={{ marginBottom: "12px" }} />
          <p style={{ margin: 0, fontSize: "13px" }}>Consultando conversaciones del cliente en BigQuery...</p>
        </div>
      )}

      {overview && (
        <>
          {/* Fila de Tarjetas KPI + Aviso Contextual de Ventana */}
          <ConversationKpiGrid
            kpis={overview.kpis}
            capabilities={overview.capabilities}
          />

          {/* Cómo terminaron */}
          <ConversationDistributionChart
            distribution={overview.distribution}
          />

          {/* Por día */}
          <ConversationDailyTrendChart
            data={overview.daily}
            dateRange={overview.date_range}
            timezone={activeTimezone}
          />

          {/* Por vendedor */}
          <ConversationAgentTable
            agents={overview.agents}
          />

          {/* Por qué se fueron */}
          <ConversationLostReasonsTable
            lostReasons={overview.lost_reasons}
          />

          {/* Curación */}
          <ConversationReviewPanel
            curation={overview.curation}
          />
        </>
      )}
    </div>
  );
};
